import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useFilterContext } from '../context/FilterContext';
import { getFilterSummary, getPresetFilters, getDisplayedGamesCount } from '../utils/filterUtils';
import TeamSearch from './TeamSearch';
import ConferenceTypeToggle from './ConferenceTypeToggle';
import FilterLogicToggle from './FilterLogicToggle';
import styles from './FilterModal.module.css';

const FilterModal = ({ games, isOpen, onClose }) => {
  const {
    filters,
    activePreset,
    updateFilter,
    applyPreset,
    clearFilters,
    getAvailableConferences
  } = useFilterContext();

  const [activeTab, setActiveTab] = useState('presets');

  // Helper function to get computed values
  const getComputedValues = useMemo(() => {
    return {
      availableConferences: getAvailableConferences(games),
      filterSummary: getFilterSummary(filters),
      presetFilters: getPresetFilters(),
      displayedGamesCount: getDisplayedGamesCount(games, filters)
    };
  }, [games, filters, getAvailableConferences]);

  const { availableConferences, filterSummary, presetFilters, displayedGamesCount } = getComputedValues;

  const handleConferenceToggle = (conference) => {
    const currentConferences = filters.conferences;
    const newConferences = currentConferences.includes(conference)
      ? currentConferences.filter(c => c !== conference)
      : [...currentConferences, conference];

    updateFilter('conferences', newConferences);
  };

  const handleSpreadChange = (type, value) => {
    const numValue = value === '' ? null : parseFloat(value);
    updateFilter(type, numValue);
  };

  const handleOverUnderChange = (type, value) => {
    const numValue = value === '' ? null : parseFloat(value);
    updateFilter(type, numValue);
  };

  const handleEloChange = (type, value) => {
    const numValue = value === '' ? null : parseFloat(value);
    updateFilter(type, numValue);
  };

  const handleExcitementChange = (type, value) => {
    const numValue = value === '' ? null : parseFloat(value);
    updateFilter(type, numValue);
  };

  // Helper function to render quick filter button
  const renderQuickFilterButton = (key, label) => (
    <button
      className={`${styles.quickFilterButton} ${filters[key] ? styles.active : ''}`}
      onClick={() => updateFilter(key, !filters[key])}
    >
      {label}
    </button>
  );

  // Helper function to render range input group
  const renderRangeInputGroup = (config) => (
    <div className={styles.rangeGroup}>
      <label htmlFor={config.minId}>{config.label}:</label>
      <div className={styles.rangeInputs}>
        <input
          id={config.minId}
          type="number"
          step={config.step || "0.5"}
          placeholder="Min"
          value={config.minValue || ''}
          onChange={(e) => config.minHandler(e.target.value)}
          className={styles.rangeInput}
        />
        <span>to</span>
        <input
          id={config.maxId}
          type="number"
          step={config.step || "0.5"}
          placeholder="Max"
          value={config.maxValue || ''}
          onChange={(e) => config.maxHandler(e.target.value)}
          className={styles.rangeInput}
        />
      </div>
    </div>
    );

  if (!isOpen) return null;

  // Use React Portal to render at document body level
  return createPortal(
    <>
      {/* Backdrop layer that sits behind modal but above content */}
      <button
        className={styles.modalBackdrop}
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        aria-label="Close modal"
      />

      {/* Modal overlay */}
      <div
        className={styles.modalOverlay}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
      >
        <dialog
          className={styles.modalContent}
          open
        >
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerInfo}>
            <h2>Filters</h2>
            <span className={`${styles.gamesCount} ${displayedGamesCount === 0 ? styles.zeroGames : ''}`}>
              Displayed Games: {displayedGamesCount}
            </span>
          </div>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        {/* Main Content Area */}
        <div className={styles.mainContent}>
          {/* Left Column - Filter Options */}
          <div className={styles.filterColumn}>
            {/* Tab Navigation */}
            <div className={styles.tabNavigation}>
              <button
                className={`${styles.tabButton} ${activeTab === 'presets' ? styles.active : ''}`}
                onClick={() => setActiveTab('presets')}
              >
                Presets
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === 'quick' ? styles.active : ''}`}
                onClick={() => setActiveTab('quick')}
              >
                Quick Filters
              </button>
              <button
                className={`${styles.tabButton} ${activeTab === 'custom' ? styles.active : ''}`}
                onClick={() => setActiveTab('custom')}
              >
                Custom Filters
              </button>
            </div>

            {/* Tab Content */}
            <div className={styles.tabContent}>
              {/* Quick Filters Tab */}
              {activeTab === 'quick' && (
                <div className={styles.quickFiltersTab}>
                  <div className={styles.filterSection}>
                    <h4>Game Status</h4>
                    <div className={styles.quickFilterGrid}>
                      {renderQuickFilterButton('hideCompleted', 'Hide Completed')}
                      {renderQuickFilterButton('hideInProgress', 'Hide In Progress')}
                      {renderQuickFilterButton('hideNotStarted', 'Hide Not Started')}
                    </div>
                  </div>

                  <div className={styles.filterSection}>
                    <h4>Game Type</h4>
                    <div className={styles.quickFilterGrid}>
                      {renderQuickFilterButton('top25', 'Top 25')}
                      {renderQuickFilterButton('rankedMatchups', 'Ranked Matchups')}
                      {renderQuickFilterButton('fbs', 'FBS')}
                      {renderQuickFilterButton('fcs', 'FCS')}
                    </div>
                  </div>

                  <div className={styles.filterSection}>
                    <h4>Betting Lines</h4>
                    <div className={styles.quickFilterGrid}>
                      {renderQuickFilterButton('homeUnderdogs', 'Home Underdogs')}
                      {renderQuickFilterButton('atsLockAvailable', 'ATS Available')}
                      {renderQuickFilterButton('overUnderLockAvailable', 'O/U Available')}
                    </div>
                  </div>

                  <div className={styles.filterSection}>
                    <h4>Game Quality</h4>
                    <div className={styles.quickFilterGrid}>
                      {renderQuickFilterButton('closeMatchups', 'Close Matchups')}
                      {renderQuickFilterButton('mismatches', 'Mismatches')}
                      {renderQuickFilterButton('barnBurners', 'Barn Burners')}
                    </div>
                  </div>

                  <div className={styles.filterSection}>
                    <h4>Conference Type</h4>
                    <ConferenceTypeToggle
                      value={filters.conferenceType}
                      onChange={(value) => updateFilter('conferenceType', value)}
                    />
                  </div>
                </div>
              )}

              {/* Custom Filters Tab */}
              {activeTab === 'custom' && (
                <div className={styles.customFiltersTab}>

                  <div className={styles.filterSection}>
                    <h4>Team Search</h4>
                    <TeamSearch />
                  </div>

                  <div className={styles.filterSection}>
                    <h4>Conference Selection</h4>
                    {availableConferences.length > 0 && (
                      <div className={styles.conferenceGrid}>
                        {availableConferences.map((conference) => (
                          <label key={conference} className={styles.conferenceCheckbox}>
                            <input
                              type="checkbox"
                              checked={filters.conferences.includes(conference)}
                              onChange={() => handleConferenceToggle(conference)}
                            />
                            {conference}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className={styles.filterSection}>
                    <h4>Betting Line Ranges</h4>
                    {renderRangeInputGroup({
                      label: 'Spread Range',
                      minId: 'spread-min',
                      maxId: 'spread-max',
                      minValue: filters.spreadMin,
                      maxValue: filters.spreadMax,
                      minHandler: (value) => handleSpreadChange('spreadMin', value),
                      maxHandler: (value) => handleSpreadChange('spreadMax', value)
                    })}
                    {renderRangeInputGroup({
                      label: 'Over/Under Range',
                      minId: 'over-under-min',
                      maxId: 'over-under-max',
                      minValue: filters.overUnderMin,
                      maxValue: filters.overUnderMax,
                      minHandler: (value) => handleOverUnderChange('overUnderMin', value),
                      maxHandler: (value) => handleOverUnderChange('overUnderMax', value)
                    })}
                  </div>

                  <div className={styles.filterSection}>
                    <h4>Advanced Metrics</h4>
                    {renderRangeInputGroup({
                      label: 'ELO Difference Range',
                      minId: 'elo-min',
                      maxId: 'elo-max',
                      minValue: filters.eloDifferenceMin,
                      maxValue: filters.eloDifferenceMax,
                      minHandler: (value) => handleEloChange('eloDifferenceMin', value),
                      maxHandler: (value) => handleEloChange('eloDifferenceMax', value),
                      step: "1"
                    })}
                    {renderRangeInputGroup({
                      label: 'Excitement Rating Range',
                      minId: 'excitement-min',
                      maxId: 'excitement-max',
                      minValue: filters.excitementIndexMin,
                      maxValue: filters.excitementIndexMax,
                      minHandler: (value) => handleExcitementChange('excitementIndexMin', value),
                      maxHandler: (value) => handleExcitementChange('excitementIndexMax', value),
                      step: "0.1"
                    })}
                  </div>

                  <div className={styles.filterSection}>
                    <h4>Game Options</h4>
                    <label className={styles.checkbox}>
                      <input
                        type="checkbox"
                        checked={filters.neutralSite}
                        onChange={(e) => updateFilter('neutralSite', e.target.checked)}
                      />
                      {' '}Neutral Site Games
                    </label>
                  </div>
                </div>
              )}

              {/* Presets Tab */}
              {activeTab === 'presets' && (
                <div className={styles.presetsTab}>
                  <div className={styles.presetGrid}>
                    {presetFilters.map((preset) => (
                      <button
                        key={preset.id}
                        className={`${styles.presetButton} ${activePreset === preset.id ? styles.active : ''}`}
                        onClick={() => applyPreset(preset.id)}
                      >
                        <h4>{preset.name}</h4>
                        <p>{preset.description}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Active Filters */}
          <div className={styles.activeFiltersColumn}>
            <div className={styles.activeFiltersHeader}>
              <h4>Active Filters</h4>
              <div className={styles.filterControls}>
                <button className={styles.clearButton} onClick={clearFilters}>
                  Clear All
                </button>
                <FilterLogicToggle
                  value={filters.filterLogic}
                  onChange={(value) => updateFilter('filterLogic', value)}
                />
              </div>
            </div>

            <div className={styles.activeFiltersList}>
              {filterSummary.length > 0 ? (
                filterSummary.map((filter, index) => (
                  <span key={`filter-${filter}-${index}`} className={styles.activeFilter}>
                    {filter}
                  </span>
                ))
              ) : (
                <span className={styles.noFilters}>No active filters</span>
              )}
            </div>
          </div>
        </div>
        </dialog>
      </div>
    </>,
    document.body
  );
};

FilterModal.propTypes = {
  games: PropTypes.array.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default FilterModal;
